'use client';

import { useEffect, useRef } from 'react';
import useSWRInfinite from 'swr/infinite';
import { useHandleError } from '@/hooks/useHandleError';
import { useDeviceType } from '@/hooks/useIsMobile';
import { apiFetch } from '@/lib/api/apiClient';
import { useLabTabStore } from '@/stores/useLabTabStore';
import { LabItem, LabListResponse } from '@/types/labs';
import ImageAdaptive from '@/components/ImageAdaptive';
import SearchResultCounter from '@/components/SearchResultCounter';
import LabTabSwitcher from '@/components/features/lab/LabTabSwitcher';
import ServiceHeader from '@/components/layout/service/ServiceHeader';
import LabListSearchResults from '@/(routers)/labs/list/components/LabListSearchResults';

export default function LabList() {
    const { isMobile } = useDeviceType();
    const { handleError, clearError } = useHandleError();
    const selectedTab = useLabTabStore((s) => s.selectedTab);
    const pageSize = isMobile ? 4 : 10;

    const getKey = (pageIndex: number, previousPageData?: LabListResponse) => {
        if (previousPageData && previousPageData.lab_list.length === 0) return null;

        const queryParams = new URLSearchParams();
        queryParams.append('lab_type', selectedTab);
        queryParams.append('limit', pageSize.toString());
        if (previousPageData) {
            const lastLab = previousPageData.lab_list[previousPageData.lab_list.length - 1];
            queryParams.append('max_published_date_time', lastLab.published_date_time);
            queryParams.append('max_id', lastLab.lab_id.toString());
        }
        return `/labs/list?${queryParams.toString()}`;
    };

    const fetcher = async (url: string) => {
        clearError();
        try {
            return await apiFetch(url, { method: 'GET' });
        } catch (error: unknown) {
            handleError(error);
            throw error;
        }
    };

    const { data, setSize, isValidating } = useSWRInfinite<LabListResponse>(getKey, fetcher);
    const searchResultList: LabItem[] = data ? data.flatMap((page) => page.lab_list) : [];
    const isLoadingMore = isValidating && searchResultList.length > 0;
    const isReachingEnd = data && data[data.length - 1]?.lab_list.length < pageSize;

    const loadMoreRef = useRef(null);
    useEffect(() => {
        if (!loadMoreRef.current) return;
        const observer = new IntersectionObserver(
            (entries) => {
                const firstEntry = entries[0];
                if (firstEntry.isIntersecting && !isLoadingMore && !isReachingEnd) {
                    setSize((prevSize) => prevSize + 1);
                }
            },
            { threshold: 1 },
        );
        observer.observe(loadMoreRef.current);
        return () => observer.disconnect();
    }, [isLoadingMore, isReachingEnd, setSize]);

    return (
        <>
            <ServiceHeader title="Lab List." />
            <div className="flex justify-center w-full">
                <div className="flex flex-col-reverse md:flex-row justify-center lg:justify-end items-start lg:items-end w-full md:w-[95%] flex-shrink-0 px-2 lg:px-0 py-2">
                    <div className="flex justify-start md:justify-end mt-2 md:mt-0 w-full md:w-auto h-8 md:h-12">
                        <SearchResultCounter
                            prefixLabel="検索結果"
                            suffixLabel="件"
                            allCount={Array.isArray(data) && data.length > 0 ? data[0].count : 0}
                        />
                    </div>
                    <div className="flex flex-grow" />
                    <div className="flex justify-center md:justify-end items-center flex-shrink-0 w-full md:w-[500px]">
                        <LabTabSwitcher />
                    </div>
                </div>
            </div>
            <div className="flex justify-center w-full pb-12">
                <LabListSearchResults dataList={searchResultList} />
            </div>
            {isLoadingMore && (
                <div className="flex justify-center items-center w-full py-4">
                    <div key="loading" className="flex justify-center items-center">
                        <ImageAdaptive
                            src="/images/common/loading-circle.svg"
                            baseWidth={80}
                            breakpoints={{ md: 100 }}
                        />
                    </div>
                </div>
            )}
            <div ref={loadMoreRef} style={{ height: '1px' }} />
        </>
    );
}
